import React from 'react';
import { TableColumn } from 'tea-component';
import { useIntl } from 'umi';
import ColumnsCenter from '@/pages/intelligence/components/TableComponents/components/CloumsCenter';
import SortBy from '@/pages/intelligence/components/TableComponents/components/SortBy';
import {
  DataSourceBubbleInfo,
  DATASOURCE_NAME,
} from '@/pages/intelligence/components/DataSourceComponents';
import TableShowData from '@/pages/intelligence/components/TableComponents/components/TableShowData';
import {
  valueFormat,
  growthFormat,
  tableDataFormatKMB,
} from '@/pages/intelligence/utils/commonUtils';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import { Content, SimpleTextInfoTips, DownloadsInfoTips, MentionsInfoTips } from './Tips';
import { COLUMNS_SORT_METADATAS_MAPPING } from '../../../constants';
import type {
  ITopChartsTypeOfCompanies,
  ITopChartsCompaniesColumnType,
  ITopChartsCompaniesRank,
} from '../../../types';
import type { IGameType } from '@/pages/intelligence/types/common';

export interface IColumnMetaData {
  dataSources: string[]; // 前端表头展示数据源icon用的数据源
  dataSource: string; // 后端排序用的数据源
  key: string;
  title: string;
  content: React.ReactNode;
  tipText: React.ReactNode;
}

// Companies榜单所有的列 元数据
export const COLUMNS_METADATAS_MAPPING: {
  [key: string]: IColumnMetaData;
} = {
  SUM_REVENUE: {
    dataSources: [],
    ...COLUMNS_SORT_METADATAS_MAPPING.SUM_REVENUE,
    content: '',
    tipText: <SimpleTextInfoTips intlKey="dbi_company_tables_info_tips1" />,
  },
  MOBILE_REVENUE: {
    dataSources: ['sensortower'],
    ...COLUMNS_SORT_METADATAS_MAPPING.MOBILE_REVENUE,
    content: <Content showMobileDataSourceRangeInfo title="sum" />,
    tipText: '',
  },
  PC_REVENUE: {
    dataSources: ['mscience', 'npd'],
    ...COLUMNS_SORT_METADATAS_MAPPING.PC_REVENUE,
    content: <Content title="sum" />,
    tipText: <SimpleTextInfoTips intlKey="dbi_company_tables_info_tips2" />,
  },
  CONSOLE_REVENUE: {
    dataSources: ['mscience', 'npd'],
    ...COLUMNS_SORT_METADATAS_MAPPING.CONSOLE_REVENUE,
    content: <Content title="sum" />,
    tipText: <SimpleTextInfoTips intlKey="dbi_company_tables_info_tips2" />,
  },
  DOWNLOADS: {
    dataSources: ['sensortower'],
    ...COLUMNS_SORT_METADATAS_MAPPING.DOWNLOADS,
    content: <Content showMobileDataSourceRangeInfo title="sum" />,
    tipText: (
      <DownloadsInfoTips
        dataSource={'sensortower'}
        intlKey="dbi_company_downloads_mentions_info_tips"
      />
    ),
  },
  MENTIONS: {
    dataSources: [],
    ...COLUMNS_SORT_METADATAS_MAPPING.MENTIONS,
    content: '',
    tipText: <MentionsInfoTips intlKey="dbi_company_downloads_mentions_info_tips" />,
  },
};

// Companies榜单所有的列 元数据
export const COLUMNS_METADATAS_MAPPINGS: any = (dataSource: string) => {
  return {
    DOWNLOADS: {
      dataSources: [dataSource || 'sensortower'],
      ...COLUMNS_SORT_METADATAS_MAPPING.DOWNLOADS,
      content: <Content showMobileDataSourceRangeInfo title="sum" />,
      tipText: (
        <DownloadsInfoTips
          dataSource={dataSource || 'sensortower'}
          intlKey="dbi_company_downloads_mentions_info_tips"
        />
      ),
    },
  };
};
// Companies榜单页面 各自的列元数据
export const PAGE_COLUMNS_METADATAS_MAPPING: {
  [key in ITopChartsTypeOfCompanies]: IColumnMetaData[];
} = {
  top_companies_revenue: [COLUMNS_METADATAS_MAPPING.SUM_REVENUE],
  top_companies_downloads: [COLUMNS_METADATAS_MAPPINGS('sensortower').DOWNLOADS],
  top_companies_mentions: [COLUMNS_METADATAS_MAPPING.MENTIONS],
};

export const PAGE_COLUMNS_METADATAS_MAPPINGS: any = (dataSource: string) => {
  return {
    top_companies_revenue: [COLUMNS_METADATAS_MAPPING.SUM_REVENUE],
    top_companies_downloads: [COLUMNS_METADATAS_MAPPINGS(dataSource).DOWNLOADS],
    top_companies_mentions: [COLUMNS_METADATAS_MAPPING.MENTIONS],
  };
};

const PAGE_DOMAINS_MAPPING: {
  [key in ITopChartsTypeOfCompanies]: string;
} = {
  top_companies_revenue: 'dbi_company_company_revenue',
  top_companies_downloads: 'dbi_company_company_downloads',
  top_companies_mentions: '',
};

export function CompaniesMetricColumn(
  params: ITopChartsCompaniesColumnType,
): TableColumn<ITopChartsCompaniesRank> {
  const {
    topChartsType,
    dataSources,
    dataSource,
    key,
    title,
    content,
    tipText,
    ratioSortable = true,
    sortMetric,
    currentSortKey,
    currentSortValue,
    onSortChage,
    entityType,
    metricsource,
    mertricKey,
  } = params || {};

  const { formatMessage } = useIntl();
  const isMentions = topChartsType === 'top_companies_mentions';
  const translatedTitle = formatMessage({ id: title });
  const translatedDomainText = (() => {
    const translateKey = PAGE_DOMAINS_MAPPING[topChartsType];
    const pageDomainName = translateKey ? formatMessage({ id: translateKey }) : '';

    return (
      <>
        {dataSources?.length === 1 && <img src={getDataSourceIcon(dataSources?.[0])} alt="" />}
        {pageDomainName}
      </>
    );
  })();

  const list: any[] = [
    {
      key: `${key}_0`,
      sortMetric,
      orderSource: dataSource,
      text: translatedTitle,
      title: translatedTitle,
      ratioType: 0,
    },
  ];

  if (ratioSortable) {
    list.push({
      key: `${key}_1`,
      sortMetric,
      orderSource: dataSource,
      text: `${translatedTitle}:Change(%)`,
      title: `${translatedTitle}(%)`,
      ratioType: 1,
    });
  }

  return {
    key,
    width: 270,
    align: 'center',
    header: () => {
      const andText = formatMessage({ id: 'dbi_company_and' });
      return (
        <ColumnsCenter width={270} align={'right'}>
          <SortBy
            title={translatedTitle}
            content={
              content && tipText ? (
                <>
                  {content}
                  <br />
                  {tipText}
                </>
              ) : (
                <>{content || tipText}</>
              )
            }
            // tipText={tipText}
            tipText=""
            svgIcon={!isMentions && getDataSourceIcon(dataSources[0])}
            svgIcon1={!isMentions && getDataSourceIcon(dataSources[1])}
            svgTip={
              key === 'total_revenue'
                ? ''
                : !isMentions && (
                    <>
                      <DataSourceBubbleInfo
                        dataOfDom={translatedDomainText}
                        dataSource={metricsource || dataSource}
                        metricKey={mertricKey || sortMetric}
                        entityType={entityType}
                        comesFormDom={
                          <>
                            {dataSources.map((item) => {
                              const dataSourceIcon = getDataSourceIcon(item);
                              return <img src={dataSourceIcon} alt="" />;
                            })}
                            {dataSources
                              .map((dataSource) => DATASOURCE_NAME?.[dataSource])
                              .join(andText)}
                          </>
                        }
                      />
                    </>
                  )
            }
            list={list}
            defaultKey={currentSortKey}
            defaultValue={currentSortValue}
            onChange={onSortChage}
          />
        </ColumnsCenter>
      );
    },
    render: (cvm) => {
      const metrics = cvm?.source_metric_list || [];
      const metric = metrics?.find((item) => item?.metric === sortMetric);
      const metricKey = metric?.metric;
      const metricValue = metric?.value;
      const formattedMetricValue =
        Number.isNaN(metricValue) || metricValue === 0 ? '-' : valueFormat(metricValue);
      const metricGrowthRatio = metric?.growth_ratio;
      const metricGrowthDataObj =
        Number.isNaN(metricGrowthRatio) || metricGrowthRatio === 0
          ? {
              type: '',
              value: '-',
            }
          : growthFormat({
              value: metricGrowthRatio,
              valueType: 'pointValue',
              hasPoint: 'lessThanOne',
              fixedNum: 2,
              maxNumber: 99999,
            });
      return (
        <ColumnsCenter width={270} align={'center'}>
          {metricKey === COLUMNS_METADATAS_MAPPING.MENTIONS.key ? (
            <div className="table-row">
              <span className="table-row-title">
                {tableDataFormatKMB(metricValue as number).toString()}
              </span>
            </div>
          ) : (
            // @ts-ignore
            <TableShowData
              value={formattedMetricValue}
              type={metricGrowthDataObj?.type}
              growth={metricGrowthDataObj?.value}
              maxNumber={'99999+%'}
            />
          )}
        </ColumnsCenter>
      );
    },
  };
}

// 获取revenue列表
export const getRevenueTableList = (gameTypes: string[]) => {
  let titles = PAGE_COLUMNS_METADATAS_MAPPING.top_companies_revenue || [];
  try {
    // 追加revenue的动态列配置，根据勾选的gameType不同
    const keys: {
      [key in IGameType]: string;
    } = {
      mobile: 'MOBILE_REVENUE',
      pc: 'PC_REVENUE',
      console: 'CONSOLE_REVENUE',
      combine: '',
    };
    const revenueTitles =
      gameTypes?.map((gameType: string) => {
        const key = keys[gameType];
        return COLUMNS_METADATAS_MAPPING[key];
      }) || [];
    titles = [...titles, ...revenueTitles];
    return titles;
  } catch (err) {
    return titles;
  }
};
